import type { WebGLRGBA } from '@pn/services/color/webglColor';
import FRAG_SRC from '@pn/services/map/mapbox/custom/shaders/hatch-polygon.frag.glsl?raw';
import VERT_SRC from '@pn/services/map/mapbox/custom/shaders/polygon.vert.glsl?raw';
import { MercatorCoordinate } from 'mapbox-gl';
import type { PatternModule, UniformLocations } from '.';

type HatchPatternProps = {
  tileSize: number;
  lineWidth: number;
  color: WebGLRGBA;
};

enum HatchMode {
  Cross = 0,
  Diag45 = 1,
  Diag135 = 2,
}

function makeHatchModule(mode: HatchMode): PatternModule<HatchPatternProps> {
  return {
    vert: VERT_SRC,
    frag: FRAG_SRC,

    init(gl, program): UniformLocations {
      return {
        _uPhasePx: gl.getUniformLocation(program, 'u_phasePx'),
        _uTileSize: gl.getUniformLocation(program, 'u_tileSize'),
        _uLineWidth: gl.getUniformLocation(program, 'u_lineWidth'),
        _uColor: gl.getUniformLocation(program, 'u_lineColor'),
        _uDpr: gl.getUniformLocation(program, 'u_dpr'),
        _uHatchMode: gl.getUniformLocation(program, 'u_hatchMode'),
      };
    },

    setUniforms({ map, gl, dpr }, uLocs, props) {
      gl.uniform1f(uLocs._uTileSize, props.tileSize);
      gl.uniform1f(uLocs._uLineWidth, props.lineWidth);
      gl.uniform4fv(uLocs._uColor, props.color);
      gl.uniform1f(uLocs._uDpr, dpr);
      gl.uniform1i(uLocs._uHatchMode, mode);

      // phase logic
      const tileSize = Math.max(props.tileSize, 1);
      const zoom = map.getZoom();
      const worldSizePx = 512 * Math.pow(2, zoom);
      const topLeft = map.unproject([0, 0]);
      const w = MercatorCoordinate.fromLngLat(
        { lng: topLeft.lng, lat: topLeft.lat },
        0
      );

      const topLeftWorldPxX = w.x * worldSizePx;
      const topLeftWorldPxY = w.y * worldSizePx;

      const phaseX = ((topLeftWorldPxX % tileSize) + tileSize) % tileSize;
      const phaseY =
        (tileSize - (((topLeftWorldPxY % tileSize) + tileSize) % tileSize)) %
        tileSize;

      gl.uniform2f(uLocs._uPhasePx, phaseX, phaseY);
    },
  };
}

export const CrossHatchPatternModule = makeHatchModule(HatchMode.Cross);
export const HatchDiag45PatternModule = makeHatchModule(HatchMode.Diag45);
export const HatchDiag135PatternModule = makeHatchModule(HatchMode.Diag135);
